import { useState } from 'react';
import {
  HISTORY_CHANGE_KINDS,
  HISTORY_ENTITY_TYPES,
  toWireDate,
  type HistoryChangeKind,
  type HistoryEntry,
  type HistoryValue,
} from '@forecastify/shared';
import {
  AvatarFace,
  Button,
  DataList,
  Dialog,
  Icon,
  type DataListColumn,
  type DataListRow,
  type ListFilter,
  type MenuItem,
  type ToolbarAction,
} from '@forecastify/ui';
import { useDuration } from '../../duration';
import { useI18n, type Translate } from '../../i18n';
import { listLabels } from '../delivery/clients-screen';
import { useCalendarLabels } from '../date-field';
import { identityColor, initialsOf } from '../identity';
import { ScreenState, type ScreenFailure } from '../screen-state';
import { COMMENT_PROSE } from '../tasks/comment-composer';
import type { ListQuery } from '../use-list-query';
import { sanitiseHtml } from './history-html';
import {
  channelLabel,
  entityText,
  entityTypeLabel,
  fieldLabel,
  formatInstant,
  kindLabel,
  summaryText,
  valueText,
  verbLabel,
} from './history-labels';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ ux-pass-2 · EVERY history in the product, and the workspace audit log.     │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * The owner: *"Both the audit log, and the history for all entities should be a searchable,
 * filterable list table everywhere. They also need a unified look."* Before this there were four
 * looks — a task's mono key list, the contract's sentences, the person's activity feed, and an
 * invoice's three lines fabricated from the record's own state — over three wire shapes. Now one
 * `DataList` with the same five columns, the same filters and the same detail, wherever it sits.
 *
 * ⚠️⚠️ **THE DETAIL OPENS IN A CENTRED DIALOG, LIKE AN ALERT'S — 17 Oct 2026, the product owner.**
 * It replaced the list in place with a back button (the sync log's run detail), which lost the
 * reader's place in the list. Now it is `AlertDetailDialog`'s shape: a modal over the list, wide
 * enough for the before → after table, closed back onto the same page and filters. A side drawer
 * stays rejected for the old reason — the history often already sits inside one.
 *
 * ⚠️ **Newest first, and that is the only order.** The server owns it (`historyQuerySchema`), so
 * no column carries a `sortKey` — a header that looks like a sort control and re-orders one page
 * of four hundred rows is `data-list.tsx`'s own warning.
 *
 * ⚠️ **A screen-level composition, not a `packages/ui` component.** It knows what a history entry
 * is, which CLAUDE.md's frontend rule keeps out of the domain-agnostic kit; the kit's `DataList`
 * does the drawing.
 */

export interface HistoryListProps {
  readonly entries: readonly HistoryEntry[];
  readonly loading: boolean;
  readonly failure: ScreenFailure | null;
  readonly onRetry: () => void;
  readonly query: ListQuery;
  /** The *Ki* filter's options. Empty, and the filter is not offered. */
  /** The *Ki* filter's people — deactivated ones included, their changes are in the log. */
  readonly members: readonly { readonly id: string; readonly displayName: string }[];
  /**
   * The *Mi* column and the entity-type filter. The audit page and a project — whose history
   * spans its members, costs and contracts (U-9) — show it; a task's own tab does not need to say
   * *Feladat · HSO-142* on every row.
   */
  readonly showEntity: boolean;
  /** The entity-type filter — the audit page only. */
  readonly entityFilter?: boolean;
  /** Opens a record at its workspace-relative `entity.path`. Absent, nothing offers to. */
  readonly onOpenPath?: (path: string) => void;
  /** Where `DataList` remembers the column choice. */
  readonly columnsKey: string;
  readonly actions?: ToolbarAction[];
}

/**
 * ⚠️ **Status hues, the design system's own** (`readme.md` — *"status hues appear in … badges"*):
 * a creation in the ok hue, a deletion in the bad one, a change neutral. Soft fill and the hue's
 * readable text, the pair `app-shell.tsx` already uses for its own state chip.
 */
const KIND_TONE: Readonly<Record<HistoryChangeKind, string>> = {
  create: 'bg-[var(--status-ok-soft)] text-[var(--status-ok-text)]',
  update: 'bg-secondary text-secondary-foreground',
  delete: 'bg-[var(--status-bad-soft)] text-[var(--status-bad-text)]',
  other: 'bg-muted text-muted-foreground',
};

const BADGE = 'rounded-md px-1.5 py-px text-[11px] font-medium';

export function KindBadge({ kind }: { readonly kind: HistoryChangeKind }) {
  const { t } = useI18n();
  return (
    <span className={`${BADGE} ${KIND_TONE[kind]} whitespace-nowrap`}>{kindLabel(t, kind)}</span>
  );
}

export function HistoryList(props: HistoryListProps) {
  const { t, locale } = useI18n();
  /* 3 Nov 2026 · an hours change reads in the reader's unit (`decisions.md` DU-1). */
  const { unit } = useDuration();
  const calendar = useCalendarLabels();
  /* ⚠️ The ENTRY, not its id: paging or a live refetch may take the row off the page while somebody
     is reading it, and the detail must not vanish from under them. */
  const [opened, setOpened] = useState<HistoryEntry | null>(null);
  const { query } = props;

  const detail =
    opened === null ? null : (
      <Dialog
        open
        onOpenChange={(next) => {
          if (!next) setOpened(null);
        }}
        title={t('history.detail.title')}
        width="min(760px, 94vw)"
        closeLabel={t('common.close')}
      >
        <HistoryDetail
          entry={opened}
          {...(props.onOpenPath === undefined
            ? {}
            : {
                onOpenPath: (path: string) => {
                  setOpened(null);
                  props.onOpenPath?.(path);
                },
              })}
        />
      </Dialog>
    );

  const rows: DataListRow[] = props.entries.map((entry) => {
    const verb = verbLabel(t, entry.action);
    return {
      id: entry.id,
      cells: {
        at: { text: formatInstant(entry.at, locale), muted: true, mono: true },
        actor: actorCell(t, entry),
        entity: { text: entityText(t, entry) },
        action: {
          /* ⚠️ The badge is the cell's text, toned — `Cell.badge` takes only the kit's four
             variants, and none of them is the ok hue. */
          text: kindLabel(t, entry.kind),
          className: `${BADGE} ${KIND_TONE[entry.kind]}`,
          ...(verb === null ? {} : { sub: verb }),
        },
        change: { text: summaryText(t, locale, entry, unit), muted: entry.changes.length === 0 },
      },
    };
  });

  const columns: DataListColumn[] = [
    { key: 'at', label: t('history.column.at'), width: 150 },
    { key: 'actor', label: t('history.column.actor'), width: 180 },
    ...(props.showEntity ? [{ key: 'entity', label: t('history.column.entity'), width: 260 }] : []),
    { key: 'action', label: t('history.column.action'), width: 190 },
    { key: 'change', label: t('history.column.change') },
  ];

  const filtered =
    query.search.trim() !== '' || Object.values(query.filters).some((value) => value !== '');

  const rangeChip = (value: string): string => {
    const [from = '', to = ''] = value.split('..');
    const show = (bound: string): string => valueText(t, locale, { kind: 'date', text: bound });
    if (from !== '' && to !== '') return t('list.rangeBoth', { from: show(from), to: show(to) });
    if (from !== '') return t('list.rangeFromOnly', { from: show(from) });
    return t('list.rangeToOnly', { to: show(to) });
  };

  const filters: ListFilter[] = [
    {
      key: 'kind',
      /* 3 Nov 2026 · these three pick several — `historyQuerySchema` reads comma lists. */
      kind: 'multi',
      label: t('history.filter.kind'),
      value: query.filters.kind ?? '',
      onChange: (value) => {
        query.setFilter('kind', value);
      },
      options: HISTORY_CHANGE_KINDS.map((kind) => ({ value: kind, label: kindLabel(t, kind) })),
    },
    ...(props.members.length === 0
      ? []
      : [
          {
            key: 'actorMemberId',
            kind: 'multi' as const,
            label: t('history.filter.actor'),
            value: query.filters.actorMemberId ?? '',
            onChange: (value: string) => {
              query.setFilter('actorMemberId', value);
            },
            options: props.members
              .map((member) => ({ value: member.id, label: member.displayName }))
              .sort((a, b) => a.label.localeCompare(b.label, locale)),
          },
        ]),
    ...(props.entityFilter === true
      ? [
          {
            key: 'entityType',
            kind: 'multi' as const,
            label: t('history.filter.entity'),
            value: query.filters.entityType ?? '',
            onChange: (value: string) => {
              query.setFilter('entityType', value);
            },
            options: HISTORY_ENTITY_TYPES.map((type) => ({
              value: type,
              label: entityTypeLabel(t, type),
            })).sort((a, b) => a.label.localeCompare(b.label, locale)),
          },
        ]
      : []),
    {
      key: 'at',
      label: t('history.filter.at'),
      value: query.filters.at ?? '',
      onChange: (value) => {
        query.setFilter('at', value);
      },
      kind: 'range',
      input: 'date',
      calendar,
      today: toWireDate(new Date()),
      options: [],
      display: rangeChip,
    },
  ];

  const byId = new Map(props.entries.map((entry) => [entry.id, entry]));
  const { pages } = query;

  return (
    <div data-history-list className="flex min-h-0 flex-1 flex-col">
      <ScreenState failure={props.failure} onRetry={props.onRetry}>
        <DataList
          columnsKey={props.columnsKey}
          card={{
            title: 'entity',
            context: 'at',
            defaults: { avatar: 'actor', details: ['action', 'change'] },
          }}
          selectable={false}
          isLoading={props.loading}
          columns={columns}
          rows={rows}
          labels={{ ...listLabels(t), table: t('history.title'), search: t('history.search') }}
          search={query.search}
          onSearchChange={query.setSearch}
          filters={filters}
          {...(props.actions === undefined ? {} : { actions: props.actions })}
          page={{
            hasPrevious: pages.hasPrevious,
            hasNext: pages.hasNext,
            onPrevious: pages.previous,
            onNext: pages.next,
            index: pages.index,
            count: pages.count,
            total: pages.total,
            onGoTo: pages.goTo,
            pageSize: pages.limit,
          }}
          onRowClick={(id) => {
            const entry = byId.get(id);
            if (entry !== undefined) setOpened(entry);
          }}
          rowMenu={(id) => rowMenu(t, byId.get(id), setOpened, props.onOpenPath)}
          empty={
            filtered
              ? {
                  icon: 'search',
                  title: t('history.noMatch.title'),
                  body: t('history.noMatch.body'),
                  actionLabel: t('list.clearAll'),
                  onAction: query.clearFilters,
                }
              : { icon: 'clock', title: t('history.empty.title'), body: t('history.empty.body') }
          }
        />
      </ScreenState>
      {detail}
    </div>
  );
}

function rowMenu(
  t: Translate,
  entry: HistoryEntry | undefined,
  open: (entry: HistoryEntry) => void,
  onOpenPath: ((path: string) => void) | undefined,
): MenuItem[] {
  if (entry === undefined) return [];
  const path = entry.entity.path;
  return [
    {
      label: t('history.detail.title'),
      icon: 'file-text',
      onSelect: () => {
        open(entry);
      },
    },
    ...(path === null || onOpenPath === undefined
      ? []
      : [
          {
            label: t('history.open'),
            icon: 'arrow-right' as const,
            onSelect: () => {
              onOpenPath(path);
            },
          },
        ]),
  ];
}

function actorCell(t: Translate, entry: HistoryEntry) {
  const name = entry.actor.name;
  if (name === null) return { text: t('history.system'), icon: 'settings' as const, muted: true };
  return {
    text: name,
    avatar: {
      initials: initialsOf(name),
      color: identityColor(entry.actor.memberId ?? name),
      title: name,
      ...(entry.actor.avatarSeed === null ? {} : { seed: entry.actor.avatarSeed }),
    },
  };
}

/* ── the detail ──────────────────────────────────────────────────────────────────────────── */

/**
 * One value, by its kind. Rich text is rendered — sanitised in the browser again
 * (`history-html.ts`) — and everything else is the formatted words.
 */
function HistoryValueView({ value }: { readonly value: HistoryValue | null }) {
  const { t, locale } = useI18n();
  const { unit } = useDuration();
  if (value !== null && value.kind === 'html' && value.text !== '') {
    return (
      <div
        className={`text-[12.5px] leading-relaxed ${COMMENT_PROSE}`}
        /* ⚠️ Sanitised on this line, by the server's own allow-list — see `history-html.ts`. */
        dangerouslySetInnerHTML={{ __html: sanitiseHtml(value.text) }}
      />
    );
  }
  const text = valueText(t, locale, value, unit);
  return (
    <span className={text === '–' ? 'text-muted-foreground/60' : 'break-words tabular-nums'}>
      {text}
    </span>
  );
}

export function HistoryDetail({
  entry,
  onBack,
  onOpenPath,
}: {
  readonly entry: HistoryEntry;
  /** Draws a back button. ⚠️ Absent inside the dialog, whose own close does that job. */
  readonly onBack?: () => void;
  readonly onOpenPath?: (path: string) => void;
}) {
  const { t, locale } = useI18n();
  const verb = verbLabel(t, entry.action);
  const path = entry.entity.path;
  const actor = entry.actor.name;

  return (
    <section
      data-history-detail
      aria-label={t('history.detail.title')}
      className="flex flex-col gap-3 px-4 py-3"
    >
      <div className="flex flex-wrap items-center gap-2">
        {onBack === undefined ? null : (
          <Button variant="ghost" size="sm" className="-ml-2 gap-1.5" onClick={onBack}>
            <Icon name="chevron-left" size={14} />
            {t('history.detail.back')}
          </Button>
        )}
        <span className="text-[13px] tabular-nums">{formatInstant(entry.at, locale)}</span>
        <KindBadge kind={entry.kind} />
        {verb === null ? null : <span className="text-muted-foreground text-[12px]">{verb}</span>}
      </div>

      <dl className="bg-card text-card-foreground grid grid-cols-[110px_minmax(0,1fr)] gap-x-3 gap-y-2 rounded-xl border p-3 text-[12.5px] shadow-sm">
        <dt className="text-muted-foreground">{t('history.column.actor')}</dt>
        <dd className="flex min-w-0 items-center gap-2">
          {actor === null ? (
            <>
              <Icon name="settings" size={14} className="text-muted-foreground" />
              <span className="text-muted-foreground">{t('history.system')}</span>
            </>
          ) : (
            <>
              <span
                aria-hidden
                className="text-on-id grid size-5 shrink-0 place-items-center overflow-hidden rounded-full text-[9px] font-semibold"
                style={{ background: identityColor(entry.actor.memberId ?? actor) }}
              >
                <AvatarFace
                  seed={entry.actor.avatarSeed ?? undefined}
                  fallback={initialsOf(actor)}
                  size={20}
                />
              </span>
              <span className="truncate">{actor}</span>
            </>
          )}
        </dd>

        <dt className="text-muted-foreground">{t('history.detail.channel')}</dt>
        <dd>{channelLabel(t, entry.channel)}</dd>

        <dt className="text-muted-foreground">{t('history.column.entity')}</dt>
        <dd className="min-w-0">
          {path === null || onOpenPath === undefined ? (
            <span className="break-words">{entityText(t, entry)}</span>
          ) : (
            <button
              type="button"
              className="text-primary inline-flex max-w-full items-center gap-1 text-left hover:underline"
              onClick={() => {
                onOpenPath(path);
              }}
            >
              <span className="break-words">{entityText(t, entry)}</span>
              <Icon name="arrow-right" size={12} className="shrink-0" />
            </button>
          )}
        </dd>
      </dl>

      {entry.changes.length === 0 ? (
        entry.note === null ? (
          <p className="text-muted-foreground text-[12.5px]">{t('history.detail.noChanges')}</p>
        ) : null
      ) : (
        <div className="bg-card text-card-foreground overflow-x-auto rounded-xl border shadow-sm">
          <table className="w-full min-w-[480px] text-[12.5px]">
            <thead>
              <tr className="text-muted-foreground bg-muted/40 border-b text-left text-[11px] font-medium tracking-wide uppercase">
                <th scope="col" className="w-[28%] px-3 py-1.5 font-medium">
                  {t('history.detail.field')}
                </th>
                <th scope="col" className="w-[36%] px-3 py-1.5 font-medium">
                  {t('history.detail.before')}
                </th>
                <th scope="col" className="w-[36%] px-3 py-1.5 font-medium">
                  {t('history.detail.after')}
                </th>
              </tr>
            </thead>
            <tbody>
              {entry.changes.map((change) => (
                <tr key={change.field} className="border-b align-top last:border-0">
                  <th scope="row" className="px-3 py-2 text-left font-medium">
                    {fieldLabel(t, change.field)}
                  </th>
                  <td className="text-muted-foreground px-3 py-2">
                    <HistoryValueView value={change.before} />
                  </td>
                  <td className="px-3 py-2">
                    <HistoryValueView value={change.after} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {entry.note === null ? null : (
        <div className="bg-card text-card-foreground rounded-xl border p-3 shadow-sm">
          <div className="text-muted-foreground text-[11px] font-medium tracking-wide uppercase">
            {t('history.detail.note')}
          </div>
          <div className="mt-1.5">
            <HistoryValueView value={entry.note} />
          </div>
        </div>
      )}
    </section>
  );
}
